import { useId, type CSSProperties } from 'react'
import type { CardStub } from '@/features/dashboard/api/schemas'
import { DeepDiveCard, type Heading } from './DeepDiveCard'
import {
  computeBlockSpans,
  splitKpis,
  type DeepDiveGroup,
} from './deepDiveLayout'
import styles from './DeepDivePage.module.css'

type CardContext = { deepDiveId: string; heading: Heading; asTable: boolean }

// A deep dive may list a card twice, so a card is keyed by where it sits.
const renderCard = (
  stub: CardStub,
  index: number,
  context: CardContext,
  style?: CSSProperties
) => (
  <DeepDiveCard
    key={`${index}-${stub.cardId}`}
    stub={stub}
    style={style}
    {...context}
  />
)

const CardGrid = ({
  group,
  context,
}: {
  group: DeepDiveGroup
  context: CardContext
}) => {
  if (group.layout === 'blocks') {
    // Equal tracks; a card's size is only its blocks.
    const spans = computeBlockSpans(group.cards, group.divisions)
    return (
      <div
        className={styles.blocks}
        style={{ gridTemplateColumns: `repeat(${group.divisions}, 1fr)` }}
      >
        {group.cards.map((stub, i) =>
          renderCard(stub, i, context, { gridColumn: `span ${spans[i]}` })
        )}
      </div>
    )
  }
  if (group.layout === 'grid') {
    return (
      <div className={styles.grid}>
        {group.cards.map((stub, i) => renderCard(stub, i, context))}
      </div>
    )
  }
  const { kpis, charts } = splitKpis(group.cards)
  return (
    <>
      {kpis.length > 0 && (
        <div className={styles.kpis}>
          {kpis.map((stub, i) => renderCard(stub, i, context))}
        </div>
      )}
      <div className={styles.grid}>
        {charts.map((stub, i) => renderCard(stub, kpis.length + i, context))}
      </div>
    </>
  )
}

export const CardGroup = ({
  group,
  deepDiveId,
  asTable,
}: {
  group: DeepDiveGroup
  deepDiveId: string
  asTable: boolean
}) => {
  const headingId = useId()
  // Under a heading, cards are titled a level below it.
  const context: CardContext = {
    deepDiveId,
    asTable,
    heading: group.heading ? 'h3' : 'h2',
  }
  const grid = <CardGrid group={group} context={context} />

  if (!group.heading) return <div className={styles.group}>{grid}</div>
  return (
    <section className={styles.group} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.groupHeading}>
        {group.heading}
      </h2>
      {grid}
    </section>
  )
}
